// @flow
import React, { PureComponent } from "react";
import { createPortal } from "react-dom";
import { Manager, Reference, Popper } from "react-popper";
import FormsyEditorBase from "./FormsyEditorBase";
import moment from "moment";
import Moment from "containers/Moment";
import LocaleUtils, { formatDate, parseDate } from "react-day-picker/moment";
import DayPickerInput from "react-day-picker/DayPickerInput";
import "react-day-picker/lib/style.css";
import loc from "i18next";
import { DateTimeFormat, type DateTimeFormatValues } from "data/types";
import { userLanguage } from "data/storeHelper";
import ClearableInput from "@hs/clearable-input";

type Props = {
    /** Field name */
    name: string,
    /** Field value */
    value: ?string,
    /** whether this field is required */
    isRequired: boolean,
    /** whether this field is readonly */
    isReadonly: boolean,
    /** how to render component */
    type: DateTimeFormatValues,
};

type State = {
    day: Date,
    time: Date,
    dateValue: string,
    timeValue: string,
};

/* istanbul ignore next */
export const DatePickerReference = React.forwardRef((inputProps, inputRef) => (
    <Reference>
        {({ ref }) => (
            <div ref={ref}>
                <ClearableInput
                    {...inputProps} multi={false} onChangeWithRawEvent={true} ref={inputRef}
                />
            </div>
        )}
    </Reference>
));

/* istanbul ignore next */
export const DatePickerPopper = ({
    children,
    classNames,
    onFocus,
    onBlur,
    tabIndex,
}) =>
    createPortal(
        <Popper
            modifiers={{
                preventOverflow: {
                    enabled: true,
                    boundariesElement: "window",
                },
                keepTogether: { enabled: true },
            }}
            positionFixed={false}
            placement="bottom-end"
        >
            {({ ref, style, placement }) => (
                <div
                    ref={ref}
                    tabIndex={tabIndex}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    style={{ ...style, zIndex: 1051 }}
                    className={classNames.overlay}
                    data-placement={placement}
                >
                    {children}
                </div>
            )}
        </Popper>,
        document.getElementById("root")
    );

export class DateEditorControl extends PureComponent<Props, State> {
    format: string;

    static defaultProps = {
        value: "",
        isRequired: false,
        isReadonly: false,
        type: DateTimeFormat.Date,
    };

    constructor(props: Props) {
        super(props);
        // set user's locale for parsing/formatting
        moment.locale(userLanguage());
        // full date&time format
        this.format = this._getFormat(props);
        // parse pristine value or set NOW
        if (props.value != null && String(props.value).length > 0) {
            const dt = moment(props.value, this.format);
            this.state = {
                day: dt.toDate(),
                time: dt.toDate(),
                dateValue: this.getValueAsDate(props.value),
                timeValue: this.getValueAsDate(
                    props.value,
                    moment.HTML5_FMT[
                    props.type.toLowerCase().includes("long")
                        ? "TIME_SECONDS"
                        : "TIME"
                    ]
                ),
            };
        } else {
            this.state = { day: new Date(), time: new Date() };
        }
    }

    _getFormat = (props) => {
        switch (props.type) {
            case DateTimeFormat.Time:
                return moment.localeData().longDateFormat("LT");
            case DateTimeFormat.LongTime:
                return moment.localeData().longDateFormat("LTS");
            case DateTimeFormat.DateTime:
                return `${moment.localeData().longDateFormat("L")} ${moment
                    .localeData()
                    .longDateFormat("LT")}`;
            case DateTimeFormat.DateLongTime:
                return `${moment.localeData().longDateFormat("L")} ${moment
                    .localeData()
                    .longDateFormat("LTS")}`;
            case DateTimeFormat.Date:
                return moment.localeData().longDateFormat("L");
            case DateTimeFormat.JsonDate:
                return `YYYY-MM-DD[T]00:00:00.000`;
            case DateTimeFormat.JsonDateEnd:
                return `YYYY-MM-DD[T]23:59:59.000`;
            case DateTimeFormat.JsonDateTime:
                return `YYYY-MM-DD[T]HH:mm:ss.SSS`;
            default:
                console.error(
                    "Wrong DateTimeFormat provided. Defaulting to DatetimeFormat.Date"
                );
                return moment.localeData().longDateFormat("L");
        }
    };

    _getDate = () => {
        const { day, time } = this.state;
        const m = moment(
            new Date(
                day.getFullYear(),
                day.getMonth(),
                day.getDate(),
                time.getHours(),
                time.getMinutes(),
                time.getSeconds()
            )
        );
        return m.format(this.format);
    };

    _onDayChange = (
        day: ?Date,
        modifiers: Object,
        dayPickerInput: DayPickerInput,
        changeValue: Function
    ) => {
        const input = dayPickerInput.getInput();
        const value =
            input.getValue() === '' &&
                dayPickerInput.state.value !== '' &&
                dayPickerInput.state.typedValue === '' ?
                dayPickerInput.state.value :
                input.getValue()

        if (day != null) {
            this.setState(
                {
                    day,
                    dateValue: this.getValueAsDate(value, undefined, "L"),
                },
                () =>
                    changeValue({
                        currentTarget: {
                            value:
                                !this.shouldRenderTime() ||
                                    this.getValueAsDate(this.state.timeValue) !== ""
                                    ? this._getDate()
                                    : this.invalidate(value),
                        },
                    })
            );
        } else {
            // typedValue should be the right property, but this callback
            // is called before setState is finished, so we have to access
            // the current value ourselfs by using input._ref.value
            //
            // if typedValue is empty => field is cleared
            // if typedValue is filled it is an invalid value
            const errorValue = input._ref?.value === '' ? '' : value
            if (errorValue.trim().length === 0 && !this.shouldRenderTime()) {
                changeValue({
                    currentTarget: { value: null },
                });
                this.setState({ dateValue: null });
            }
            else {
                if (!this.props.isRequired && this.state.timeValue === '__INVALID__' && errorValue.trim().length === 0) {
                    changeValue({
                        currentTarget: {
                            value: null
                        }
                    });
                    this.setState({
                        dateValue: null
                    });
                } else {
                    changeValue({
                        currentTarget: {
                            value: this.invalidate(errorValue),
                        },
                    });
                    this.setState({ dateValue: this.invalidate(errorValue) });
                }
            }
        }
    };

    _onTimeChange = (event: SyntheticInputEvent<*>, changeValue: Function) => {
        const value = event.currentTarget.value;
        const time = moment(value, "LTS").toDate();
        if (
            value.trim().length > 0 &&
            !isNaN(time) &&
            (!this.shouldRenderSeconds() || value.match(/:/g).length === 2) //HACK: do validate long time correctly
        ) {
            this.setState({ time, timeValue: value }, () =>
                changeValue({
                    currentTarget: {
                        value:
                            !this.shouldRenderDate() ||
                                this.getValueAsDate(this.state.dateValue) !== ""
                                ? this._getDate()
                                : this.invalidate(value),
                    },
                })
            );
        } else {
            if (value.trim().length === 0 && !this.shouldRenderDate()) {
                changeValue({
                    currentTarget: { value: null },
                });
                this.setState({ timeValue: null });
            } else {
                if (!this.props.isRequired && this.state.dateValue === '__INVALID__' && value.trim().length === 0) {
                    changeValue({
                        currentTarget: { value: null },
                    });
                    this.setState({ timeValue: null });
                } else {
                    changeValue({
                        currentTarget: {
                            value: this.invalidate(value),
                        },
                    });
                    this.setState({ timeValue: this.invalidate(value) });
                }
            }
        }
    };

    shouldRenderDate = () => this.props.type.toLowerCase().includes("date");
    shouldRenderTime = () => this.props.type.toLowerCase().includes("time");
    shouldRenderSeconds = () => this.props.type.toLowerCase().includes("long");

    invalidate = (value: string): string => `__INVALID__${value}`;

    /* istanbul ignore next */
    getValueAsDate = (
        value: ?string,
        format: string = "L",
        valueFormat: string = this.format
    ) =>
        value &&
            String(value).length > 0 &&
            !String(value).startsWith("__INVALID__")
            ? moment(value, valueFormat).format(format)
            : "";

    getValidValue = (value: ?string): any =>
        value && typeof value === "string"
            ? value.replace("__INVALID__", "")
            : value;

    render = () => (
        <FormsyEditorBase
            required={
                this.props.isRequired ? "isDefaultRequiredValue" : undefined
            }
            render={(
                value: ?string,
                changeValue: (name: string, value: ?string) => void,
                isValid: boolean
            ) => (
                <Manager>
                    {this.shouldRenderDate() && (
                        <DayPickerInput
                            inputProps={{
                                className: `form-control${!isValid ? " has-error" : ""
                                    }`,
                                disabled: this.props.isReadonly,
                            }}
                            required={this.props.isRequired}
                            classNames={{
                                container:
                                    "DayPickerInput container-form-control",
                                overlayWrapper: "DayPickerInput-OverlayWrapper",
                                overlay: "DayPickerInput-Overlay",
                            }}
                            formatDate={formatDate}
                            parseDate={parseDate}
                            dayPickerProps={{
                                showWeekNumbers: true,
                                numberOfMonths: 1,
                                firstDayOfWeek: 1,
                                locale: userLanguage(),
                                localeUtils: LocaleUtils,
                                fixedWeeks: true,
                                todayButton: loc.t(
                                    "folder:action_filter.calendar_today"
                                ),
                            }}
                            value={this.getValidValue(this.state.dateValue)}
                            onDayChange={
                                /* istanbul ignore next */ (
                                day: ?Date,
                                modifiers: Object,
                                dayPickerInput: DayPickerInput
                            ) =>
                                    this._onDayChange(
                                        day,
                                        modifiers,
                                        dayPickerInput,
                                        changeValue
                                    )
                            }
                            placeholder={moment
                                .localeData()
                                .longDateFormat("L")}
                            format={"L"}
                            component={DatePickerReference}
                            overlayComponent={DatePickerPopper}
                        />
                    )}
                    {this.shouldRenderTime() && (
                        <ClearableInput
                            onChangeWithRawEvent={true}
                            name={this.props.name}
                            inputType="time"
                            disabled={this.props.isReadonly}
                            value={this.getValidValue(this.state.timeValue)}
                            step={this.shouldRenderSeconds() ? 1 : 60}
                            onChange={
                            /*istanbul ignore next*/  (event) =>
                                    this._onTimeChange(event, changeValue)
                            }
                            style={{
                                flexGrow: 0
                            }}
                            className="TimePickerInput form-control"
                        />
                    )}
                </Manager>
            )}
            {...this.props}
        />
    );
}

export default Moment(DateEditorControl);
